import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { 
  CheckCircle, XCircle, Flame, ChefHat, AlertCircle, 
  ThumbsUp, ThumbsDown, Eye, Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

function ValidationCard({ cook, onVote }) {
  const [voting, setVoting] = useState(false);
  const [voted, setVoted] = useState(cook.user_voted);

  const handleVote = async (vote) => {
    if (voted) return;
    
    setVoting(true);
    try {
      const result = await onVote(cook.id, vote);
      setVoted(vote);
      toast.success(result.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du vote');
    } finally {
      setVoting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="bg-white rounded-3xl shadow-sm overflow-hidden"
    >
      {/* User & Recipe Info */}
      <div className="p-4 flex items-center justify-between border-b">
        <Link 
          to={`/u/${cook.user?.username}`}
          className="flex items-center gap-3 group"
        >
          <Avatar className="w-10 h-10 border-2 border-[#1E4ED8]/20">
            <AvatarImage src={cook.user?.profile_picture} />
            <AvatarFallback className="bg-gradient-to-br from-[#1E4ED8] to-[#60A5FA] text-white">
              {cook.user?.username?.charAt(0)}
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="font-semibold group-hover:text-[#1E4ED8] transition-colors">
              {cook.user?.display_name}
            </p>
            <p className="text-sm text-gray-500">@{cook.user?.username}</p>
          </div>
        </Link>
        
        <Link to={`/recipe/${cook.recipe_id}`}>
          <Badge className="bg-[#1E4ED8]/10 text-[#1E4ED8] hover:bg-[#1E4ED8]/20">
            {cook.recipe?.name}
          </Badge>
        </Link>
      </div>

      {/* Proof Image */}
      <div className="relative aspect-square bg-gray-100">
        <img 
          src={cook.proof_image} 
          alt="Réalisation" 
          className="w-full h-full object-cover"
        />
        
        {/* Time ago */}
        <div className="absolute top-3 left-3 bg-black/50 text-white text-xs px-3 py-1 rounded-full backdrop-blur-sm flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {new Date(cook.created_at).toLocaleDateString('fr-FR', { 
            day: 'numeric', 
            month: 'short',
            hour: '2-digit',
            minute: '2-digit'
          })}
        </div>
      </div>

      {/* Vote Section */}
      <div className="p-4">
        {/* Current Votes */}
        <div className="flex items-center justify-center gap-6 mb-4">
          <div className="flex items-center gap-2 text-[#4F6F52]">
            <ThumbsUp className="w-5 h-5" />
            <span className="font-bold">{cook.upvotes}</span>
            <span className="text-sm text-gray-500">approuvent</span>
          </div>
          <div className="flex items-center gap-2 text-[#D9381E]">
            <ThumbsDown className="w-5 h-5" />
            <span className="font-bold">{cook.downvotes}</span>
            <span className="text-sm text-gray-500">rejettent</span>
          </div>
        </div>

        {/* Vote Buttons */}
        {voted ? (
          <div className={`text-center p-3 rounded-xl ${
            voted === 'approve' 
              ? 'bg-[#4F6F52]/10 text-[#4F6F52]' 
              : 'bg-[#D9381E]/10 text-[#D9381E]'
          }`}>
            <p className="font-medium flex items-center justify-center gap-2">
              {voted === 'approve' ? (
                <>
                  <CheckCircle className="w-5 h-5" />
                  Tu as approuvé cette réalisation
                </>
              ) : (
                <>
                  <XCircle className="w-5 h-5" />
                  Tu as rejeté cette réalisation
                </>
              )}
            </p>
          </div>
        ) : (
          <div className="flex gap-3">
            <Button
              onClick={() => handleVote('approve')}
              disabled={voting}
              className="flex-1 bg-[#4F6F52] hover:bg-[#3d5a40] text-white rounded-full"
            >
              <ThumbsUp className="w-4 h-4 mr-2" />
              Approuver
            </Button>
            <Button
              onClick={() => handleVote('reject')}
              disabled={voting}
              variant="outline"
              className="flex-1 border-[#D9381E] text-[#D9381E] hover:bg-[#D9381E]/10 rounded-full"
            >
              <ThumbsDown className="w-4 h-4 mr-2" />
              Rejeter
            </Button>
          </div>
        )}

        {/* Reward Info */}
        <p className="text-center text-xs text-gray-500 mt-3 flex items-center justify-center gap-1">
          <Flame className="w-3 h-3 text-[#D9381E]" />
          +1 BARKA pour chaque vote
        </p>
      </div>
    </motion.div>
  );
}

export default function ValidationPage() {
  const { api, isAuthenticated } = useAuth();
  const [pendingCooks, setPendingCooks] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPending = useCallback(async () => {
    try {
      const res = await api.get('/validation/pending');
      setPendingCooks(res.data.pending_cooks || []);
    } catch (error) {
      console.error('Failed to fetch pending validations:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchPending();
    }
  }, [fetchPending, isAuthenticated]);

  const handleVote = async (cookId, vote) => {
    const res = await api.post(`/validation/vote/${cookId}`, null, {
      params: { vote }
    });
    
    // If decision was made, remove from list
    if (res.data.decision) {
      setPendingCooks(pendingCooks.filter(c => c.id !== cookId));
    }
    
    return res.data;
  };

  if (!isAuthenticated) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 text-center">
        <div className="w-20 h-20 rounded-full bg-[#1E4ED8]/10 flex items-center justify-center mx-auto mb-4">
          <Eye className="w-10 h-10 text-[#1E4ED8]" />
        </div>
        <h1 className="font-fraunces text-2xl font-bold mb-2">Validation communautaire</h1>
        <p className="text-gray-600 mb-6">
          Connecte-toi pour aider à valider les réalisations de la communauté et gagner des BARKA!
        </p>
        <Button asChild className="bg-[#1E4ED8] hover:bg-[#1E40AF] rounded-full">
          <Link to="/auth">Se connecter</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6" data-testid="validation-page">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#1E4ED8] to-[#60A5FA] flex items-center justify-center mx-auto mb-4 shadow-lg">
          <Eye className="w-8 h-8 text-white" />
        </div>
        <h1 className="font-fraunces text-3xl font-bold mb-2">Validation communautaire</h1>
        <p className="text-gray-600">
          Aide à valider les réalisations culinaires et gagne des BARKA!
        </p>
      </div>

      {/* Info Card */}
      <Card className="bg-[#F8F5F2] mb-6">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-[#1E4ED8] flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-gray-900 mb-1">Comment ça marche?</p>
              <ul className="text-gray-600 space-y-1">
                <li>• Vote pour approuver ou rejeter les photos</li>
                <li>• Vérifie que la photo montre un vrai plat cuisiné</li>
                <li>• Rejette les photos d'internet ou les fakes</li>
                <li>• +1 BARKA par vote, que tu approuves ou rejettes</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pending Validations */}
      {loading ? (
        <div className="space-y-6">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="rounded-3xl">
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-4">
                  <Skeleton className="w-10 h-10 rounded-full" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                </div>
                <Skeleton className="aspect-square w-full rounded-xl" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : pendingCooks.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-20 h-20 rounded-full bg-[#4F6F52]/10 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-10 h-10 text-[#4F6F52]" />
          </div>
          <h3 className="font-fraunces text-xl font-bold mb-2">Tout est validé!</h3>
          <p className="text-gray-500">
            Reviens plus tard pour de nouvelles réalisations à valider.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          <AnimatePresence mode="popLayout">
            {pendingCooks.map((cook) => (
              <ValidationCard 
                key={cook.id} 
                cook={cook} 
                onVote={handleVote}
              />
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
